import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Upload, Loader2, FileText, Image as ImageIcon, X, Check, Film,
} from 'lucide-react';
import { api, formatApiError, absoluteUrl } from '../lib/api';

const ALL_TABS = [
  { id: 'all', label: 'Todos' },
  { id: 'image', label: 'Imágenes' },
  { id: 'video', label: 'Videos' },
  { id: 'document', label: 'Documentos' },
];

function humanSize(bytes) {
  if (!bytes) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}

/**
 * Modal to pick an existing file from the media library (or upload a new one).
 * Props:
 *   - onSelect({ url, name, type }) — fires with the chosen file.
 *   - onClose()
 *   - filterType: 'image' | 'document' | null  (locks the tabs when set)
 *   - title
 */
export default function MediaLibraryPicker({ onSelect, onClose, filterType = null, title = 'Elegir de la biblioteca' }) {
  const [files, setFiles] = useState([]);
  const [counts, setCounts] = useState({ all: 0, image: 0, video: 0, document: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(filterType || (filterType === null ? 'document' : 'all'));
  const [query, setQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const tabs = filterType ? ALL_TABS.filter((t) => t.id === filterType) : ALL_TABS;

  async function load() {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/admin/media');
      setFiles(data.files || []);
      setCounts(data.counts || { all: 0, image: 0, video: 0, document: 0 });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudieron cargar los archivos.');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const activeType = filterType || tab;
    return files.filter((f) => (
      (activeType === 'all' || f.type === activeType) &&
      (!q || f.filename.toLowerCase().includes(q))
    ));
  }, [files, tab, query, filterType]);

  async function onUpload(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      const { data } = await api.post('/admin/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
      onSelect({ url: data.url, name: data.original_filename || data.filename, type: data.type });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error al subir el archivo.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const accept = filterType === 'image'
    ? 'image/*,.svg'
    : filterType === 'video'
    ? 'video/*,.mp4,.webm,.mov,.m4v,.ogg,.ogv'
    : filterType === 'document'
    ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.txt,.csv,.zip'
    : 'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.txt,.csv,.zip,.svg,.mp4,.webm,.mov,.m4v';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" data-testid="media-picker-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200">
          <h2 className="font-bold text-charcoal-800 text-base">{title}</h2>
          <button onClick={onClose} data-testid="media-picker-close" className="p-1.5 rounded hover:bg-gray-100"><X size={16} /></button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-gray-100">
          <div className="flex gap-1">
            {tabs.map((tt) => (
              <button key={tt.id} onClick={() => setTab(tt.id)} data-testid={`media-picker-tab-${tt.id}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${(filterType || tab) === tt.id ? 'bg-cmic-50 text-cmic-700' : 'text-charcoal-500 hover:bg-gray-50'}`}>
                {tt.label}{!filterType && <span className="ml-1.5 text-[10px] bg-gray-100 rounded-full px-1.5 py-0.5">{counts[tt.id] ?? 0}</span>}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-charcoal-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar…"
                data-testid="media-picker-search" className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm w-44" />
            </div>
            <input ref={fileRef} type="file" accept={accept} onChange={onUpload} className="hidden" data-testid="media-picker-upload-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-picker-upload-btn"
              className="inline-flex items-center gap-1.5 bg-cmic-600 hover:bg-cmic-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-60">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Subiendo…' : 'Subir nuevo'}
            </button>
          </div>
        </div>

        {error && <div className="mx-5 mt-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-2 text-xs">{error}</div>}

        <div className="p-5 overflow-y-auto">
          {loading ? (
            <div className="py-16 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>
          ) : visible.length === 0 ? (
            <p className="py-16 text-center text-charcoal-500 text-sm" data-testid="media-picker-empty">
              {query ? 'Sin resultados. Prueba otra búsqueda o sube un archivo nuevo.' : 'No hay archivos todavía. Sube el primero con “Subir nuevo”.'}
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-testid="media-picker-grid">
              {visible.map((f) => {
                const isImg = f.type === 'image' && f.extension !== 'svg';
                const url = absoluteUrl(f.url);
                return (
                  <button key={f.filename} onClick={() => onSelect({ url: f.url, name: f.filename, type: f.type })}
                    data-testid={`media-picker-item-${f.filename}`}
                    className="group text-left bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-cmic-400 hover:ring-2 hover:ring-cmic-100 transition-all">
                    <div className="relative aspect-square bg-charcoal-50 flex items-center justify-center overflow-hidden">
                      {isImg ? (
                        <img src={url} alt={f.filename} loading="lazy" className="w-full h-full object-cover" />
                      ) : f.extension === 'svg' ? (
                        <img src={url} alt={f.filename} loading="lazy" className="w-2/3 h-2/3 object-contain" />
                      ) : f.type === 'video' ? (
                        <video src={url} muted preload="metadata" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center gap-1 text-charcoal-400">
                          <FileText size={26} />
                          <span className="text-[9px] font-bold uppercase">{f.extension}</span>
                        </div>
                      )}
                      {f.type === 'video' && (
                        <span className="absolute bottom-1 right-1 bg-black/60 text-white rounded p-0.5"><Film size={11} /></span>
                      )}
                      <span className="absolute inset-0 bg-cmic-600/0 group-hover:bg-cmic-600/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                        <span className="bg-cmic-600 text-white rounded-full p-1.5"><Check size={14} /></span>
                      </span>
                    </div>
                    <div className="p-1.5">
                      <p className="text-[10px] font-medium text-charcoal-700 truncate" title={f.filename}>{f.filename}</p>
                      <p className="text-[9px] text-charcoal-400 flex items-center gap-1">
                        {f.type === 'image' ? <ImageIcon size={9} /> : f.type === 'video' ? <Film size={9} /> : <FileText size={9} />}{humanSize(f.size_bytes)}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
